import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Searchbar from "../../components/Search";
import useApiPost from "../hooks/postData";
import toast from "react-hot-toast";
import Loader from "../../assets/Loader.gif";
import Add from "../../assets/Add.png";
import NoData from "../../assets/NoData.png";
import CpCategoryFormDialog from "./CpCategoryFormDialog";

const headCell = "text-headerText font-poppins text-sm font-semibold";

function CpConnectionCategory() {
  const isSidebarOpen = useSelector((state) => state.sidebar.isOpen);

  const { data, postData, loading } = useApiPost();
  const { postData: postToggle } = useApiPost();
  const { postData: postDelete } = useApiPost();

  const [isLoading, setIsLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      await postData("/cp-connection-category/list", {});
    } catch (err) {
      toast.error("Failed to load categories");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = data?.categories || [];

  const openAddModal = () => {
    setEditingCategory(null);
    setOpenForm(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setOpenForm(true);
  };

  const handleFormSaved = async () => {
    setOpenForm(false);
    await fetchCategories();
  };

  const handleToggleActive = async (category) => {
    setTogglingId(category.id);
    try {
      const response = await postToggle("/cp-connection-category/edit", {
        id: category.id,
        is_active: category.is_active ? 0 : 1,
      });
      if (response?.response_code === "1") {
        await fetchCategories();
      } else {
        toast.error(response?.message || "Failed to update status");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (category) => {
    if (
      !window.confirm(
        `Delete "${category.name}"? Existing connections keep their category snapshot. You can also just disable it instead.`
      )
    )
      return;
    try {
      const response = await postDelete("/cp-connection-category/delete", { id: category.id });
      if (response?.response_code === "1") {
        toast.success("Category deleted");
        await fetchCategories();
      } else {
        toast.error(response?.message || "Delete failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  return (
    <>
      <div className={`dark:bg-primary ${isSidebarOpen ? "xl:pl-20" : "xl:pl-72"}`}>
        <Searchbar />

        <div className="px-4 pb-10 xl:px-6">
          <div className="flex justify-between border-t-[#F2F2F2] py-3">
            <h2 className="text-[#000000] dark:text-darkText font-poppins text-xl font-semibold pt-3">
              CP Categories
            </h2>
            <button
              className="flex gap-1.5 place-items-center px-4 text-sm font-poppins font-medium text-[#FFFFFF] rounded-md bg-button-gradient"
              onClick={openAddModal}
            >
              <img src={Add} className="w-4 h-4" alt="" />
              <p>Add Category</p>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <Link to="/dashboard">
              <h3 className="text-[#3A3A3A] dark:text-darkText font-poppins text-base font-semibold">
                Dashboard
              </h3>
            </Link>
            <div className="rounded-full w-1 h-1 bg-[#E0E0E0]"></div>
            <h3 className="text-[#858585] font-poppins text-base">CP Categories</h3>
          </div>

          <div className="border border-[#E3E3E3] dark:border-[#1F1F1F] rounded-lg mt-8 overflow-x-auto w-full">
            <div className="min-w-[900px]">
              <div className="flex px-4 py-3 pl-8 text-left border-b dark:border-b-[#1F1F1F] dark:bg-darkHeader bg-header1">
                <div className={`w-[8%] ${headCell}`}>S.L</div>
                <div className={`w-[27%] ${headCell}`}>NAME</div>
                <div className={`w-[10%] ${headCell}`}>EMOJI</div>
                <div className={`w-[15%] ${headCell}`}>COINS</div>
                <div className={`w-[12%] ${headCell}`}>ORDER</div>
                <div className={`w-[13%] ${headCell}`}>STATUS</div>
                <div className={`w-[15%] ${headCell}`}>ACTIONS</div>
              </div>

              {isLoading || loading ? (
                <div className="flex justify-center py-32">
                  <img src={Loader} alt="loader" height={50} width={50} />
                </div>
              ) : categories.length === 0 ? (
                <div className="flex flex-col justify-center py-32 place-items-center">
                  <img src={NoData} alt="no data" className="w-96" />
                  <h2 className="font-poppins text-[#000000] dark:text-darkText text-sm font-semibold">
                    No categories yet. Click "Add Category" to create one.
                  </h2>
                </div>
              ) : (
                categories.map((cat, index) => (
                  <div
                    key={cat.id}
                    className={`${
                      index % 2 === 0 ? "bg-white dark:bg-primary" : "bg-[#00162e0a] dark:bg-primary"
                    } flex items-center px-4 py-3 pl-8 border-b dark:border-b-[#1F1F1F] last:border-0`}
                  >
                    <div className="w-[8%] text-[#000000] dark:text-darkText font-poppins text-sm">
                      {index + 1}
                    </div>
                    <div className="w-[27%] text-[#000000] dark:text-darkText font-poppins text-sm font-semibold">
                      {cat.name}
                    </div>
                    <div className="w-[10%] text-xl">{cat.emoji || "-"}</div>
                    <div className="w-[15%] text-[#414141] dark:text-tableDarkLarge font-poppins text-sm">
                      {Number(cat.coin_cost).toLocaleString()}
                    </div>
                    <div className="w-[12%] text-[#414141] dark:text-tableDarkLarge font-poppins text-sm">
                      {cat.sort_order}
                    </div>
                    <div className="w-[13%]">
                      <button
                        onClick={() => handleToggleActive(cat)}
                        disabled={togglingId === cat.id}
                        title="Click to enable / disable"
                        className={`px-3 py-1 text-xs font-semibold rounded-full font-poppins ${
                          cat.is_active
                            ? "text-[#0D9947] bg-[#0D9947] bg-opacity-20"
                            : "text-red-500 bg-red-500 bg-opacity-20"
                        }`}
                      >
                        {togglingId === cat.id ? "..." : cat.is_active ? "Active" : "Inactive"}
                      </button>
                    </div>
                    <div className="w-[15%] flex items-center gap-2">
                      <button
                        className="px-3 py-1 text-xs bg-[#F2F2F2] dark:bg-[#1F1F1F] dark:text-darkText rounded-md font-poppins"
                        onClick={() => openEditModal(cat)}
                      >
                        Edit
                      </button>
                      <button
                        className="px-3 py-1 text-xs text-white bg-red-500 rounded-md font-poppins"
                        onClick={() => handleDelete(cat)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <CpCategoryFormDialog
        open={openForm}
        handleClose={() => setOpenForm(false)}
        onSaved={handleFormSaved}
        editingCategory={editingCategory}
      />
    </>
  );
}

export default CpConnectionCategory;
